import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import SendMessageModal from "./SendMessageModal";

const SendMessageContext = createContext(null);

const INITIAL_STATE = {
  isOpen: false,
  borrower: null,
  initialTemplateKey: undefined,
  initialVariables: undefined,
  initialIsCollection: false,
  caseNotes: "",
  channelHealth: [],
};

/**
 * @typedef {Object} SendMessageOptions
 * @property {object} [borrower]
 * @property {string} [borrowerId]
 * @property {string} [templateKey] alias for initialTemplateKey
 * @property {string} [initialTemplateKey]
 * @property {Record<string, string>} [variables] alias for initialVariables
 * @property {Record<string, string>} [initialVariables]
 * @property {boolean} [isCollection] alias for initialIsCollection
 * @property {boolean} [initialIsCollection]
 * @property {string} [caseNotes]
 * @property {Array} [channelHealth]
 * @property {() => void} [onSent]
 */

function resolveBorrower(options = {}) {
  if (options.borrower) {
    return options.borrower;
  }
  if (options.borrowerId) {
    return { id: options.borrowerId };
  }
  return null;
}

export function SendMessageProvider({ children }) {
  const [state, setState] = useState(INITIAL_STATE);
  const onSentRef = useRef(null);

  const closeSendMessage = useCallback(() => {
    setState(INITIAL_STATE);
    onSentRef.current = null;
  }, []);

  const openSendMessage = useCallback(
    /** @param {SendMessageOptions} [options] */
    (options = {}) => {
      onSentRef.current = options.onSent ?? null;
      setState({
        isOpen: true,
        borrower: resolveBorrower(options),
        initialTemplateKey: options.initialTemplateKey ?? options.templateKey,
        initialVariables: options.initialVariables ?? options.variables,
        initialIsCollection: options.initialIsCollection ?? options.isCollection ?? false,
        caseNotes: options.caseNotes ?? "",
        channelHealth: options.channelHealth ?? [],
      });
    },
    []
  );

  const handleSent = useCallback(() => {
    onSentRef.current?.();
  }, []);

  const value = useMemo(
    () => ({ openSendMessage, closeSendMessage }),
    [closeSendMessage, openSendMessage]
  );

  return (
    <SendMessageContext.Provider value={value}>
      {children}
      <SendMessageModal
        isOpen={state.isOpen}
        toggle={closeSendMessage}
        borrower={state.borrower}
        initialTemplateKey={state.initialTemplateKey}
        initialVariables={state.initialVariables}
        initialIsCollection={state.initialIsCollection}
        caseNotes={state.caseNotes}
        channelHealth={state.channelHealth}
        onSent={handleSent}
      />
    </SendMessageContext.Provider>
  );
}

export function useSendMessage() {
  const ctx = useContext(SendMessageContext);
  if (!ctx) {
    throw new Error("useSendMessage must be used within SendMessageProvider");
  }
  return ctx;
}
